import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, apiPut, apiDelete, apiPost } from '../lib/api'
// @ts-ignore
import type ReactGridLayout from 'react-grid-layout'

// ── Types ────────────────────────────────────────────────────────────

export interface WidgetSettings {
    showLastMonth?: boolean
    showLastYear?: boolean
    showPctChange?: boolean
    showNetChange?: boolean
    chartCompareTo?: 'none' | 'lastMonth' | 'lastYear'
    showDiscounts?: boolean
    sortBy?: 'name' | 'number' | 'performance'
}

export interface WidgetMeta {
    id: string
    label: string
    description: string
    category: 'KPI' | 'Chart' | 'Table'
    dashboardTypes: string
    defaultW: number
    defaultH: number
    isEnabled: boolean
}

/** Default dimensions for each widget at full size (based on 40 columns) */
export const DEFAULT_WIDGET_DIMS: Record<string, { w: number, h: number }> = {
    netSales: { w: 10, h: 10 }, margin: { w: 10, h: 10 }, costOfSales: { w: 10, h: 10 }, transactions: { w: 10, h: 10 },
    unitsSold: { w: 10, h: 10 }, avgTransaction: { w: 10, h: 10 }, upt: { w: 10, h: 10 }, discountDepth: { w: 10, h: 10 },
    sellThrough: { w: 10, h: 10 }, inventoryValue: { w: 10, h: 10 }, gmroi: { w: 10, h: 10 },
    channelMix: { w: 40, h: 20 }, hourlyChart: { w: 30, h: 20 }, dailyChart: { w: 30, h: 20 }, paymentMix: { w: 10, h: 10 },
    topSellers: { w: 40, h: 20 }, soldOut: { w: 40, h: 20 },
    salesByStore: { w: 40, h: 20 },
}

/** Compute actual dimensions considering dynamic size */
export function getWidgetDims(widgetId: string): { w: number, h: number } {
    let def = DEFAULT_WIDGET_DIMS[widgetId]

    // If not found in defaults, check dynamic widgets from the server
    if (!def) {
        const meta = useMonthlyDashboardLayoutStore.getState().availableWidgets?.find(w => w.id === widgetId)
        if (meta) {
            def = { w: Math.round(meta.defaultW * 2.5), h: Math.round(meta.defaultH * 2.5) }
        } else {
            def = { w: 10, h: 10 } // Absolute fallback
        }
    }

    return def
}

export interface DashboardLayoutState {
    /** The actual RGL grid properties for each breakpoint */
    layouts: Record<string, ReactGridLayout.Layout[]>
    /** Widget IDs currently on the board */
    activeWidgets: string[]
    /** Per-widget settings */
    widgetSettings: Record<string, WidgetSettings>
    /** Whether edit mode is active */
    isEditing: boolean
    /** Available widgets from the server */
    availableWidgets: WidgetMeta[]
    /** Global comparison setting for coloring KPIs */
    primaryComparison: 'none' | 'lastMonth' | 'lastYear'
    /** When true, KPI comparisons use the full period instead of period-to-date */
    compareFull: boolean
    /** Whether initial load has happened */
    loaded: boolean
    
    /** The active layout preset details */
    availableLayouts: DashboardLayoutPreset[]
    currentLayoutId: string | null
    currentLayoutName: string | null
    currentLayoutIsDefault: boolean

    /** The user ID whose layout is stored — safety net to detect missed logouts */
    storedUserId: string | null
    /** Previous layouts for undo snapshot (single move) */
    previousLayouts: Record<string, ReactGridLayout.Layout[]> | null
    /** State captured before entering edit mode for "Undo All" */
    preEditState: {
        layouts: Record<string, ReactGridLayout.Layout[]>
        activeWidgets: string[]
        widgetSettings: Record<string, WidgetSettings>
        primaryComparison: 'none' | 'lastMonth' | 'lastYear'
        compareFull: boolean
    } | null
    /** Monotonic counter — incremented on reset to force react-grid-layout remount */
    layoutVersion: number

    /** Call on logout — wipes ALL user state so it never leaks to the next user */
    clearUserState: () => void
    setEditing: (editing: boolean) => void
    addWidget: (id: string) => void
    removeWidget: (id: string) => void
    setLayouts: (layouts: Record<string, ReactGridLayout.Layout[]>) => void
    snapshotLayouts: () => void
    undoLayouts: () => void
    revertToInitial: () => void
    updateWidgetSettings: (id: string, settings: Partial<WidgetSettings>) => void
    setPrimaryComparison: (comp: 'none' | 'lastMonth' | 'lastYear') => void
    setCompareFull: (val: boolean) => void
    resetLayout: () => void
    resetToSystemDefault: (token: string, userId: string, dashboardType?: string) => Promise<void>
    setAvailableWidgets: (widgets: WidgetMeta[]) => void
    loadFromServer: (token: string, userId: string, dashboardType?: string) => Promise<void>
    loadLayout: (token: string, userId: string, dashboardType?: string, id?: string | null) => Promise<void>
    saveToAccount: (token: string, name: string | null, isDefault: boolean, isNew: boolean, dashboardType?: string) => Promise<void>
    saveAsSystemDefault: (token: string, dashboardType?: string, overwriteUsers?: boolean) => Promise<void>
    hasUnsavedChanges: () => boolean
    applyImportedLayout: (parsed: { layouts: Record<string, any[]>; activeWidgets: string[]; widgetSettings: Record<string, WidgetSettings>; primaryComparison?: string; compareFull?: boolean }) => void
}

export interface DashboardLayoutPreset {
    id: string
    name: string | null
    isDefault: boolean
    sortOrder: number
}

// ── Default layout ───────────────────────────────────────────────────

const DEFAULT_ACTIVE_WIDGETS = [
    'netSales', 'margin', 'costOfSales', 'transactions',
    'unitsSold', 'avgTransaction', 'upt', 'discountDepth',
    'sellThrough', 'inventoryValue', 'gmroi',
    'channelMix', 'dailyChart', 'paymentMix',
    'topSellers', 'soldOut', 'salesByStore'
]

const DEFAULT_WIDGET_SETTINGS: Record<string, WidgetSettings> = {}
DEFAULT_ACTIVE_WIDGETS.forEach(id => {
    DEFAULT_WIDGET_SETTINGS[id] = {
        showLastMonth: true,
        showLastYear: true,
        showPctChange: true,
        showNetChange: true,
        chartCompareTo: 'lastMonth',
        showDiscounts: true
    }
})

export function generateDefaultLayouts(): Record<string, ReactGridLayout.Layout[]> {
    const lg: ReactGridLayout.Layout[] = []
    const xs: ReactGridLayout.Layout[] = []
    let xl = 0, yl = 0, xx = 0, yx = 0
    DEFAULT_ACTIVE_WIDGETS.forEach((id) => {
        const dims = getWidgetDims(id)
        if (xl + dims.w > 40) { xl = 0; yl += dims.h }
        lg.push({ i: id, x: xl, y: yl, w: dims.w, h: dims.h })
        xl += dims.w

        const xw = dims.w >= 20 ? 10 : 5
        const xh = dims.h >= 20 ? 10 : Math.max(5, Math.ceil(dims.h / 2))
        if (xx + xw > 10) { xx = 0; yx += xh }
        xs.push({ i: id, x: xx, y: yx, w: xw, h: xh })
        xx += xw
    })
    return { lg, xs }
}

// ── Store ────────────────────────────────────────────────────────────

export const useMonthlyDashboardLayoutStore = create<DashboardLayoutState>()(
    persist(
        (set, get) => ({
            layouts: generateDefaultLayouts(),
            activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
            widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
            primaryComparison: 'none',
            compareFull: false,
            isEditing: false,
            availableWidgets: [],
            loaded: false,
            availableLayouts: [],
            currentLayoutId: null,
            currentLayoutName: null,
            currentLayoutIsDefault: false,
            storedUserId: null,
            previousLayouts: null,
            preEditState: null,
            layoutVersion: 0,

            // ── Called on logout: wipes ALL user-specific state ─────────────────
            clearUserState: () => set((s) => ({
                layouts: generateDefaultLayouts(),
                activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                primaryComparison: 'none',
                compareFull: false,
                isEditing: false,
                availableWidgets: [],
                loaded: false,
                availableLayouts: [],
                currentLayoutId: null,
                currentLayoutName: null,
                currentLayoutIsDefault: false,
                storedUserId: null,
                previousLayouts: null,
                preEditState: null,
                layoutVersion: s.layoutVersion + 1,
            })),

            setEditing: (editing) => {
                const state = get()
                if (editing && !state.isEditing) {
                    // Capture state before editing starts
                    set({
                        isEditing: true,
                        preEditState: {
                            layouts: JSON.parse(JSON.stringify(state.layouts)),
                            activeWidgets: [...state.activeWidgets],
                            widgetSettings: JSON.parse(JSON.stringify(state.widgetSettings)),
                            primaryComparison: state.primaryComparison,
                            compareFull: state.compareFull,
                        }
                    })
                } else if (!editing) {
                    set({ isEditing: false, preEditState: null })
                }
            },

            addWidget: (id) => {
                const state = get()
                if (state.activeWidgets.includes(id)) return

                // Put it at the bottom
                const lgLayouts = state.layouts.lg || []
                const maxY = lgLayouts.reduce((max, l) => Math.max(max, l.y + l.h), 0)
                const dims = getWidgetDims(id)
                const newLayoutItem = { i: id, x: 0, y: maxY, w: dims.w, h: dims.h }

                const xsLayouts = state.layouts.xs || []
                const maxYxs = xsLayouts.reduce((max, l) => Math.max(max, l.y + l.h), 0)
                const xw = dims.w >= 20 ? 10 : 5
                const xh = dims.h >= 20 ? 10 : Math.max(5, Math.ceil(dims.h / 2))
                const newXsItem = { i: id, x: 0, y: maxYxs, w: xw, h: xh }

                set({
                    activeWidgets: [...state.activeWidgets, id],
                    layouts: {
                        ...state.layouts,
                        lg: [...lgLayouts, newLayoutItem],
                        xs: [...xsLayouts, newXsItem]
                    },
                    widgetSettings: {
                        ...state.widgetSettings,
                        [id]: { showLastMonth: true, showLastYear: true },
                    },
                })
            },

            removeWidget: (id) => {
                const state = get()
                set({
                    activeWidgets: state.activeWidgets.filter(w => w !== id),
                    layouts: {
                        ...state.layouts,
                        lg: (state.layouts.lg || []).filter(l => l.i !== id),
                        xs: (state.layouts.xs || []).filter(l => l.i !== id)
                    }
                })
            },

            setLayouts: (layouts) => {
                set({ layouts })
            },

            snapshotLayouts: () => {
                const state = get()
                set({ previousLayouts: JSON.parse(JSON.stringify(state.layouts)) })
            },

            undoLayouts: () => {
                const state = get()
                if (state.previousLayouts) {
                    set({
                        layouts: state.previousLayouts,
                        previousLayouts: null,
                    })
                }
            },

            revertToInitial: () => {
                const state = get()
                if (state.preEditState) {
                    set({
                        layouts: state.preEditState.layouts,
                        activeWidgets: state.preEditState.activeWidgets,
                        widgetSettings: state.preEditState.widgetSettings,
                        primaryComparison: state.preEditState.primaryComparison,
                        compareFull: state.preEditState.compareFull,
                        isEditing: false,
                        preEditState: null,
                    })
                }
            },

            updateWidgetSettings: (id, settings) => {
                const state = get()
                set({
                    widgetSettings: {
                        ...state.widgetSettings,
                        [id]: { ...state.widgetSettings[id], ...settings },
                    },
                })
            },

            setPrimaryComparison: (comp) => set({ primaryComparison: comp }),
            setCompareFull: (val) => set({ compareFull: val }),

            resetLayout: () => set({
                layouts: generateDefaultLayouts(),
                activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                primaryComparison: 'none',
                compareFull: false,
                previousLayouts: null,
                preEditState: null,
            }),

            resetToSystemDefault: async (token, userId, dashboardType = 'monthly') => {
                // Attempt to delete the user's personal layout so the system default is returned.
                // Swallow errors — the user may never have saved a personal layout, which is fine.
                try {
                    await apiDelete(`/api/dashboard/layout?type=${dashboardType}`, token)
                } catch { /* ignore — proceed with local reset regardless */ }

                // Only reset control flags — don't write hardcoded defaults to the
                // persisted store. loadFromServer will apply the server layout.
                set({ loaded: false, isEditing: false, previousLayouts: null, preEditState: null })
                await get().loadFromServer(token, userId, dashboardType)
                // Bump version to force react-grid-layout Responsive to remount
                // and pick up the new layouts (it caches layouts per breakpoint internally).
                set((s) => ({ layoutVersion: s.layoutVersion + 1 }))
            },

            setAvailableWidgets: (widgets) => set({ availableWidgets: widgets }),

            loadFromServer: async (token, userId, dashboardType = 'monthly') => {
                try {
                    // Load available widgets
                    const widgetData = await api(`/api/dashboard/widgets?type=${dashboardType}`, token)
                    if (widgetData?.widgets) {
                        set({ availableWidgets: widgetData.widgets })
                    }

                    const state = get()

                    // Safety net: if a different user's data is somehow in the store
                    // (e.g. logout didn't fire), wipe it before loading
                    const userChanged = state.storedUserId !== null && state.storedUserId !== userId
                    if (userChanged) {
                        set({
                            layouts: generateDefaultLayouts(),
                            activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                            widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                            primaryComparison: 'none',
                            compareFull: false,
                            isEditing: false,
                            loaded: false,
                            storedUserId: null,
                            previousLayouts: null,
                            preEditState: null,
                        })
                    }

                    // Load presets
                    try {
                        const layoutRes = await api(`/api/dashboard/layouts?type=${dashboardType}`, token)
                        if (layoutRes?.layouts) {
                            set({ availableLayouts: layoutRes.layouts })
                        }
                    } catch (e) { console.warn('[dashboard] Failed to load preset list:', e) }

                    // Load from server if we don't have a valid layout cached for this user
                    if (!get().loaded) {
                        const layoutData = await api(`/api/dashboard/layout?type=${dashboardType}`, token)
                        if (layoutData?.layoutJson) {
                            try {
                                const parsed = JSON.parse(layoutData.layoutJson)
                                if (parsed.activeWidgets) {
                                    set({
                                        layouts: parsed.layouts ?? generateDefaultLayouts(),
                                        activeWidgets: parsed.activeWidgets,
                                        widgetSettings: parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                                        primaryComparison: parsed.primaryComparison ?? 'none',
                                    compareFull: parsed.compareFull ?? false,
                                        loaded: true,
                                        storedUserId: userId,
                                    })
                                } else {
                                    console.warn('[dashboard] Server layout JSON has no activeWidgets:', layoutData.layoutJson)
                                }
                            } catch (e) {
                                console.warn('[dashboard] Failed to parse server layout JSON:', e, layoutData.layoutJson)
                            }
                        } else {
                            console.warn('[dashboard] No layoutJson returned from server, source:', layoutData?.source)
                        }
                        // Mark loaded even if no server layout existed (will show hardcoded default)
                        set((s) => s.loaded ? {} : { loaded: true, storedUserId: userId })
                    }
                } catch (e) {
                    console.warn('[dashboard] loadFromServer failed:', e)
                }
            },

            loadLayout: async (token, userId, dashboardType = 'monthly', id = null) => {
                try {
                    let endpoint = `/api/dashboard/layout?type=${dashboardType}`
                    if (id === 'system-default') endpoint += `&systemDefault=true`
                    else if (id) endpoint += `&id=${id}`

                    const layoutData = await api(endpoint, token)
                    if (layoutData?.layoutJson) {
                        try {
                            const parsed = JSON.parse(layoutData.layoutJson)
                            if (parsed.activeWidgets) {
                                set({
                                    layouts: parsed.layouts ?? generateDefaultLayouts(),
                                    activeWidgets: parsed.activeWidgets,
                                    widgetSettings: parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                                    primaryComparison: parsed.primaryComparison ?? 'none',
                                    compareFull: parsed.compareFull ?? false,
                                    currentLayoutId: layoutData.id ?? null,
                                    currentLayoutName: layoutData.name ?? null,
                                    currentLayoutIsDefault: layoutData.isDefault ?? false,
                                    loaded: true,
                                    storedUserId: userId,
                                })
                            } else {
                                console.warn('[dashboard] Server layout JSON has no activeWidgets:', layoutData.layoutJson)
                            }
                        } catch (e) {
                            console.warn('[dashboard] Failed to parse server layout JSON:', e, layoutData.layoutJson)
                        }
                    } else {
                        // Clear active layout indicators
                        set({
                            currentLayoutId: null,
                            currentLayoutName: null,
                            currentLayoutIsDefault: false,
                        })
                        console.warn('[dashboard] No layoutJson returned from server, source:', layoutData?.source)
                    }
                    // Mark loaded even if no server layout existed (will show hardcoded default)
                    set((s) => s.loaded ? {} : { loaded: true, storedUserId: userId })
                } catch (e) {
                    console.warn('[dashboard] loadLayout failed:', e)
                }
            },

            saveToAccount: async (token, name, isDefault, isNew, dashboardType = 'monthly') => {
                const state = get()
                const layoutJson = JSON.stringify({
                    layouts: state.layouts,
                    activeWidgets: state.activeWidgets,
                    widgetSettings: state.widgetSettings,
                    primaryComparison: state.primaryComparison,
                    compareFull: state.compareFull,
                })
                
                await apiPost('/api/dashboard/layout', token, {
                    id: isNew ? undefined : state.currentLayoutId,
                    name: name || 'My Dashboard',
                    layoutJson,
                    dashboardType,
                    isDefault,
                    isNew
                })
                
                // Reload to get the updated ID/Preset list
                if (state.storedUserId) {
                    await get().loadFromServer(token, state.storedUserId, dashboardType)
                }
            },

            saveAsSystemDefault: async (token, dashboardType = 'monthly', overwriteUsers = false) => {
                const state = get()
                const payload = {
                    layouts: state.layouts,
                    activeWidgets: state.activeWidgets,
                    widgetSettings: state.widgetSettings,
                    primaryComparison: state.primaryComparison,
                    compareFull: state.compareFull,
                }
                const layoutJson = JSON.stringify(payload)
                console.log('[dashboard] saveAsSystemDefault — activeWidgets:', payload.activeWidgets.length, 'layout items:', payload.layouts?.lg?.length)
                const result = await apiPut('/api/admin/dashboard/layout/default', token, {
                    layoutJson,
                    dashboardType,
                    overwriteUsers,
                })
                console.log('[dashboard] saveAsSystemDefault — server response:', result)
            },

            hasUnsavedChanges: () => {
                const state = get()
                if (!state.isEditing || !state.preEditState) return false
                return (
                    JSON.stringify(state.activeWidgets) !== JSON.stringify(state.preEditState.activeWidgets) ||
                    JSON.stringify(state.layouts) !== JSON.stringify(state.preEditState.layouts) ||
                    JSON.stringify(state.widgetSettings) !== JSON.stringify(state.preEditState.widgetSettings) ||
                    state.primaryComparison !== state.preEditState.primaryComparison ||
                    state.compareFull !== state.preEditState.compareFull
                )
            },

            applyImportedLayout: (parsed) => {
                set((s) => ({
                    layouts: parsed.layouts ?? generateDefaultLayouts(),
                    activeWidgets: parsed.activeWidgets,
                    widgetSettings: parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                    primaryComparison: (parsed.primaryComparison as any) ?? 'none',
                    compareFull: parsed.compareFull ?? false,
                    currentLayoutId: null,
                    currentLayoutName: null,
                    currentLayoutIsDefault: false,
                    layoutVersion: s.layoutVersion + 1,
                    preEditState: {
                        layouts: JSON.parse(JSON.stringify(parsed.layouts ?? generateDefaultLayouts())),
                        activeWidgets: [...parsed.activeWidgets],
                        widgetSettings: JSON.parse(JSON.stringify(parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS })),
                        primaryComparison: (parsed.primaryComparison as any) ?? 'none',
                        compareFull: parsed.compareFull ?? false,
                    },
                }))
            },
        }),
        {
            name: 'puresoft-monthly-dashboard-layout',
            version: 9, // v9: added compareFull to layout persistence
            migrate: (persistedState: any, _version: number) => {
                const state = persistedState as Partial<DashboardLayoutState>
                return {
                    layouts: state.layouts ?? generateDefaultLayouts(),
                    activeWidgets: state.activeWidgets ?? [...DEFAULT_ACTIVE_WIDGETS],
                    widgetSettings: state.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                    primaryComparison: state.primaryComparison ?? 'none',
                    compareFull: state.compareFull ?? false,
                    loaded: false,
                    storedUserId: state.storedUserId ?? null,
                    previousLayouts: null,
                    preEditState: null,
                }
            },
            // `loaded` is intentionally NOT included — it must reset to false on every
            // page load so the server layout is always fetched after login.
            // Layout data is persisted only as a quick-render cache while the fetch is in-flight.
            partialize: (state) => ({
                layouts: state.layouts,
                activeWidgets: state.activeWidgets,
                widgetSettings: state.widgetSettings,
                primaryComparison: state.primaryComparison,
                compareFull: state.compareFull,
                storedUserId: state.storedUserId,
                currentLayoutId: state.currentLayoutId,
                currentLayoutName: state.currentLayoutName,
                currentLayoutIsDefault: state.currentLayoutIsDefault,
            }) as any,
        }
    )
)
